import Moment from 'moment';

// The API sends start_date/end_date/start_time/end_time as UTC ISO strings
// (e.g. "2026-10-25T00:00:00.000+00:00"), but the values themselves are the
// literal date/wall-clock time to show - they are not meant to be converted
// to whatever timezone the viewer's browser happens to be in. Parsing them
// with plain `Moment(value)` silently shifts them to local time: a browser
// west of UTC can show start_date a day earlier than the API sent, and any
// browser not exactly on UTC shows the wrong hour for start_time/end_time.
// Using `.utc(value)` instead reads the literal date/time out of the string
// with no shift, so what's on screen always matches the API response.
//
// The API's event start_time/end_time also carry their own (often
// unrelated) date component - e.g. an event with start_date 2026-10-25 can
// have start_time "2026-09-17T06:00:00.000+00:00". The real date always
// comes from start_date/end_date; start_time/end_time are only ever used for
// the time-of-day portion. Use formatEventDate() for the former and
// formatEventTime() for the latter, and combine them rather than formatting
// one field with a date+time pattern.
export function formatEventDate(value, format = 'MMM D YYYY') {
    if (!value) {
        return '--';
    }
    const parsed = Moment.utc(value);
    return parsed.isValid() ? parsed.format(format) : '--';
}

// Event start_time/end_time are expected to be full ISO datetimes. If the
// API ever sends a bare time-of-day string instead (e.g. "14:00:00"),
// Moment's default parser falls back to `new Date(value)`, which resolves it
// against today's date/timezone rather than the event's actual date -
// showing a wrong date, or "Invalid date" depending on the browser. Only the
// time-of-day portion is ever taken from this value, so that mismatch
// doesn't matter here.
export function formatEventTime(value, format = 'hh:mm A') {
    if (!value) {
        return '--';
    }
    const full = Moment.utc(value);
    if (full.isValid()) {
        return full.format(format);
    }
    const bareTime = Moment(value, ['HH:mm:ss', 'HH:mm', 'hh:mm A', 'hh:mm a'], true);
    return bareTime.isValid() ? bareTime.format(format) : '--';
}
